import { type FC, useState, useEffect, useMemo } from 'react';
import { Flex } from '@repo/ui/Flex';
import { Box } from '@repo/ui/Box';
import { Text } from '../Language/Language';
import { ItemCardButtonView, ClaimedItemCard } from '../ItemCard/ItemCardView';
import type { ShipmentGroup as ShipmentGroupType } from '../../utils/item';
import { expandItemsWithClaims } from '../../utils/expandItemsWithClaims';
import s from './ShipmentGroup.module.css';
import sharedStyles from '../shared/ShowMoreLink.module.css';
import type { ItemSelectConfig } from '../../graphql/query/getRetailerSettings';

interface ShipmentGroupProps {
  shipmentGroup: ShipmentGroupType;
  onItemSelect?: (itemId: string, trackingNumber?: string) => void;
  onClaimClick?: (claimId: string) => void;
  imageSize?: 'sm' | 'md' | 'lg' | 'xl';
  showShipmentDetails?: boolean;
  showMoreCutoff?: number;
  itemSelectConfig?: ItemSelectConfig | null;
}

export const ShipmentGroup: FC<ShipmentGroupProps> = ({
  shipmentGroup,
  onItemSelect,
  onClaimClick,
  imageSize = 'xl',
  showShipmentDetails = true,
  showMoreCutoff = 3,
  itemSelectConfig,
}) => {
  const { items, trackingNumber, shipDate } = shipmentGroup;
  const [showMore, setShowMore] = useState(false);

  // Expand items with claims into separate display items
  const displayItems = useMemo(() => expandItemsWithClaims(items), [items]);

  const notCutoff = displayItems.length <= showMoreCutoff;

  useEffect(() => {
    setShowMore(notCutoff);
  }, [notCutoff]);

  if (!items || items.length === 0) {
    return null;
  }

  const formatShipDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const ShowMoreButton = () => {
    if (showMore) {
      return null;
    }

    const hiddenItemCount = displayItems.length - showMoreCutoff;

    return (
      <Flex className={s.showMore}>
        <button
          onClick={() => setShowMore(true)}
          className={sharedStyles.showMoreLink}
          type="button"
        >
          <Text
            langKey="Global.showMoreItems"
            replacementTokens={{
              count: hiddenItemCount.toString(),
            }}
          />
        </button>
      </Flex>
    );
  };

  return (
    <Box className={s.shipmentGroup}>
      {showShipmentDetails && (
        <Box className={s.shipmentHeader}>
          <div className={s.shipmentTitle}>
            <Text
              langKey={
                items.length === 1
                  ? 'Global.shipment.shippedDate.singular'
                  : 'Global.shipment.shippedDate.plural'
              }
              replacementTokens={{
                itemCount: items.length.toString(),
                date: shipDate ? formatShipDate(shipDate) : 'Unknown Date',
              }}
            />
          </div>
          {trackingNumber && (
            <div className={s.trackingNumber}>
              <Text
                langKey="Global.shipment.trackingNumber"
                replacementTokens={{
                  trackingNumber: trackingNumber,
                }}
              />
            </div>
          )}
        </Box>
      )}

      <Flex direction="column" gap="g-16" className={s.items}>
        {displayItems.map((displayItem, i) => {
          if (!showMore && i > showMoreCutoff - 1) return null;

          if (displayItem.isClaimedItem && displayItem.singleClaim) {
            return (
              <ClaimedItemCard
                key={displayItem.displayId}
                displayItem={displayItem}
                imageSize={imageSize}
                itemSelectConfig={itemSelectConfig}
                onClaimClick={onClaimClick}
                ariaLabelledby={`${displayItem.displayId}-title`}
              />
            );
          }

          return (
            <ItemCardButtonView
              key={displayItem.displayId}
              onClick={() => onItemSelect?.(displayItem.id, trackingNumber)}
              aria-labelledby={`${displayItem.displayId}-title`}
              imageSize={imageSize}
              item={displayItem}
              remainingQuantity={displayItem.quantity?.remaining || 1}
              isShipmentScopeReason={false}
              itemSelectConfig={itemSelectConfig}
            />
          );
        })}
      </Flex>

      <ShowMoreButton />
    </Box>
  );
};
